import React, { FC, useCallback, useContext } from 'react';
import { Document, pdfjs } from 'react-pdf';
import { ContextApi } from 'react-uforms';
import { Row, Col } from 'styled-bootstrap-grid';
import { Question, QuestionRequired } from '@ch/common/types/data';
import { Box } from '@ch/common/components/box';
import { Button } from '@ch/common/components/button';
import { Modal } from '@ch/common/components/modal';
import { IconChevronLeft, IconChevronRight } from '@ch/common/components/icon';
import { useData } from '../use-data';
import { controlButtonProps, controlIconProps, dpiRatio } from './const';
import { useQuizDialog } from './hooks/use-quiz-dialog';
import { QuizDialogDocumentWrap, PageStyled } from './quiz.styled';
import QuizAnnotation from './quiz-annotation';
import { Text } from '@ch/common/components/text';
import Loading from '@ch/common/components/loading/loading';
import { FormInputField } from '@ch/common/components/form/input';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const QuizFieldDialog: FC<Question> = ({
  identifier,
  question,
  sub_question: subQuestion,
  required,
  file = 'assets/Pregnancy Drug Exposure.pdf',
}) => {
  const {
    data: {
      keywords: { backToPortal, next, submit, previous },
    },
  } = useData();
  const {
    isOpen,
    setIsOpen,
    onLoadSuccess: onPdfLoad,
    annotations,
    setAnnotations,
    numPages,
    pageIndex,
    setPageIndex,
    handleUpload,
  } = useQuizDialog();
  const { setValue } = useContext(ContextApi);
  const onLoadSuccess = useCallback(
    (pdf: pdfjs.PDFDocumentProxy) => {
      onPdfLoad(pdf, file);
    },
    [file, onPdfLoad],
  );
  const requiredIdentifier = `${identifier}_required`;
  const onSubmit = useCallback(async () => {
    if (await handleUpload()) {
      setValue(requiredIdentifier, 'processed');
    }
  }, [setValue, handleUpload, requiredIdentifier]);

  return (
    <Box $m={[-12, 0, 0]} data={requiredIdentifier}>
      <Box $display="flex">
        <Button $size="xs" $variant="outlined" onClick={() => setIsOpen(true)}>
          {question}
        </Button>
        {required === QuestionRequired.YES && (
          <Text as="span" $m={[0, 0, 0, 3]} $color="error" $size="lg">
            *
          </Text>
        )}
      </Box>
      <FormInputField name={requiredIdentifier} type="hidden" />
      <Modal isOpen={isOpen} $size="xl" onClose={() => false}>
        <Modal.Body>
          <QuizDialogDocumentWrap>
            <>
              <Document
                {...{
                  file: `/${file}`,
                  onLoadSuccess,
                  loading: (
                    <Box $p={[200, 0]}>
                      <Loading $fullScreen={false} />
                    </Box>
                  ),
                }}
              >
                {[...Array(numPages)].map((p, i) => (
                  <PageStyled key={i} {...{ pageIndex: i, scale: dpiRatio, $active: pageIndex === i }} />
                ))}
              </Document>
              {annotations.map(({ id, ...rest }) => (
                <QuizAnnotation key={id} {...{ id, pageIndex, ...rest }} />
              ))}
            </>
          </QuizDialogDocumentWrap>
        </Modal.Body>
        <Modal.Footer>
          <Row justifyContent="center">
            {backToPortal && (
              <Col col={2}>
                <Button
                  {...{
                    ...controlButtonProps,
                    onClick: () => {
                      setIsOpen(false);
                      setAnnotations([]);
                    },
                  }}
                >
                  {backToPortal}
                </Button>
              </Col>
            )}
            <Col col={2}>
              <Button
                {...{
                  ...controlButtonProps,
                  $variant: 'outlined',
                  disabled: pageIndex === 0,
                  onClick: () => {
                    setPageIndex(pageIndex - 1);
                    setAnnotations([]);
                  },
                }}
              >
                <IconChevronLeft {...{ ...controlIconProps, $m: [0, 10, 0, 0] }} />
                {previous}
              </Button>
            </Col>
            <Col col={2}>
              {pageIndex === numPages - 1 ? (
                <Button {...controlButtonProps} onClick={onSubmit}>
                  {submit}
                </Button>
              ) : (
                <Button
                  {...controlButtonProps}
                  onClick={() => {
                    setPageIndex(pageIndex + 1);
                  }}
                >
                  {next}
                  <IconChevronRight {...{ ...controlIconProps, $m: [0, 0, 0, 10] }} />
                </Button>
              )}
            </Col>
          </Row>
          <Text $size="xs">{subQuestion}</Text>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default QuizFieldDialog;
